import React, { useEffect, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { fromJS } from 'immutable';

import {
    fetchChannelStatSummary,
    fetchPaginatedArchivedChannelSessions,
    updateChannelMember,
} from 'flow/actions/channels-actions';
import AgentStatisticsPreview from 'flow/components/pbx/AgentStatisticsPreview';
import LiveChatActiveProfile from 'flow/components/pbx/live-chat/LiveChatActiveProfile';
import LiveChatTickets from 'flow/components/pbx/live-chat/LiveChatTickets';
import MemberCountDescription from 'flow/components/pbx/MemberCountDescription';
import PbxMemberLoggedInStatusItem from 'flow/components/pbx/PbxMemberLoggedInMemberStatus';
import PbxMenuIcon from 'flow/components/pbx/PbxMenuIcon';
import Page from 'flow/components/Setting/Page';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import Loading from 'flow/components/ui-components/Loading';
import RouteLink from 'flow/components/ui-components/RouteLink';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { Channel } from 'flow/types/Channel';
import { ChannelKey } from 'flow/types/EntityKeys';
import { PbxParams } from 'flow/types/Params';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import {
    getChannelStatisticsFavorites,
    translateNumber,
} from 'flow/utils/browser-utils';
import { getLoggedInMemberSize } from 'flow/utils/pbx-utils';

const SECONDS = 1000;

const PbxLiveChat = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { ownExtension } = useExtension();
    const { isSinglePaneMode } = useSinglePaneMode();
    const { entityKey, tabState } = useParams<PbxParams>();
    const timeoutId = useRef<NodeJS.Timeout | number>(null);

    const channelDTO: TypedMap<Channel> = useAppSelector((state) =>
        state.channels.getIn(['channels', entityKey]),
    );
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        clearInterval(timeoutId.current as number);

        dispatch(fetchChannelStatSummary(entityKey));

        timeoutId.current = setInterval(() => {
            dispatch(fetchChannelStatSummary(entityKey));
        }, 30 * SECONDS);

        if (tabState === 'archived') {
            dispatch(fetchPaginatedArchivedChannelSessions(entityKey));
        }
        return () => clearInterval(timeoutId.current as number);
    }, [dispatch, entityKey]);

    if (!channelDTO) {
        return <Loading />;
    }

    const channelMember = channelDTO
        .get('channelMemberDTOs')
        .find(
            (member) =>
                member.get('chatUserKey') === ownExtension.get('chatUserKey'),
        );

    return (
        <Page
            mainId="live-chat-page"
            header={channelDTO.get('channelName')}
            onClickBack={
                isSinglePaneMode ? () => history.push(routes['pbx']) : undefined
            }
            action={
                <IconButton
                    tooltipLabel={i18next.t('settings')}
                    onClick={() =>
                        history.push(
                            generatePath(
                                routes['pbx.channels.channelKey.settings'],
                                {
                                    channelKey: entityKey,
                                },
                            ),
                        )
                    }
                >
                    <Icon iconKey="setting_menu" />
                </IconButton>
            }
            childrenClassName="w-full p-0 !-mt-6"
        >
            {channelDTO.has('statistics') ? (
                <AgentStatisticsPreview
                    statSummary={channelDTO.get('statistics')}
                    getStatisticsFavorites={getChannelStatisticsFavorites}
                    current={current}
                    setCurrent={setCurrent}
                />
            ) : null}

            <div
                className="mx-auto w-full max-w-screen-sm overflow-y-scroll bg-white p-2"
                id="main-scrollable-div"
            >
                <div className="flex h-13 items-center gap-x-3 rounded-sm p-2">
                    <PbxMenuIcon iconKey="forum" />

                    {channelDTO.get('channelName')}
                </div>

                <LiveChatActiveProfile channelKey={entityKey as ChannelKey} />

                {channelMember ? (
                    <PbxMemberLoggedInStatusItem
                        toggleButton={
                            <Toggle
                                value={channelMember?.get('loggedIn')}
                                onChange={(loggedIn: boolean) => {
                                    dispatch(
                                        updateChannelMember(
                                            entityKey,
                                            channelMember.get('key'),
                                            fromJS({
                                                key: channelMember.get('key'),
                                                loggedIn: loggedIn,
                                            }),
                                        ),
                                    );
                                }}
                                activeColor="bg-pbx-channel"
                                text={i18next.t('pbx.loggedin')}
                            />
                        }
                    />
                ) : null}

                <RouteLink
                    to={generatePath(
                        routes['pbx.channels.channelKey.members'],
                        {
                            channelKey: entityKey,
                        },
                    )}
                    description={
                        <MemberCountDescription
                            loggedInMembers={translateNumber(
                                getLoggedInMemberSize(
                                    channelDTO.get('channelMemberDTOs'),
                                ),
                            )}
                            totalMembers={translateNumber(
                                channelDTO.get('channelMemberDTOs').size,
                            )}
                        />
                    }
                >
                    <PbxMenuIcon iconKey="headset" />

                    <div>{i18next.t('Queue.HandleMembers.Title')}</div>
                </RouteLink>
                <LiveChatTickets className="mt-4" />
            </div>
        </Page>
    );
};

export default PbxLiveChat;
